/**
 * OptionsStrategyPanel — Sprint 22.7.29 (Module 5 thesis-to-trade + education).
 *
 * Renders /api/options/strategies: per-underlying options STRUCTURE
 * recommendations with transparent reasoning chains + education. Rule-based,
 * structural guidance (KORINTH has no options-chain feed — see footer note).
 * Mirrors the RiskEngine.jsx fetch + 120s-refresh + ModuleContainer pattern.
 */
import { useCallback, useEffect, useState } from 'react';
import { Activity, BookOpen, Clock } from 'lucide-react';
import ModuleContainer from '../layout/ModuleContainer.jsx';

const TYPE_COLOR = { income: '#10b981', directional: '#3b82f6', hedge: '#f59e0b', vol: '#a855f7' };
const BIAS_COLOR = { bullish: '#10b981', bearish: '#ef4444', neutral: '#6b7280', unknown: '#6b7280' };
const IV_COLOR = { high: '#ef4444', moderate: '#f59e0b', low: '#10b981' };
const MUTED = '#6b7280';

function Chip({ label, value, color }) {
  if (value == null || value === '') return null;
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded border border-korinth-border bg-korinth-surface-2">
      <span className="text-korinth-muted">{label}</span>
      <span style={{ color: color || '#fff' }} className="font-bold">{value}</span>
    </span>
  );
}

function StrategyCard({ card, primary }) {
  if (!card) return null;
  const tcolor = TYPE_COLOR[card.strategy_type] || MUTED;
  return (
    <div className={`rounded border ${primary ? 'border-korinth-accent/40' : 'border-korinth-border'} bg-korinth-surface-2 p-2.5 space-y-2`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-sm font-bold text-white">{card.strategy_name}</span>
          <span className="text-[9px] font-mono font-bold uppercase px-1.5 py-0.5 rounded" style={{ color: tcolor, backgroundColor: `${tcolor}1a` }}>{card.strategy_type}</span>
          {!primary && <span className="text-[9px] font-mono text-korinth-muted uppercase">alternative</span>}
        </div>
      </div>
      <div className="text-[11px] font-mono text-korinth-muted">{card.legs}</div>
      {card.strike_expiry_guidance && (
        <div className="text-[11px] text-korinth-text"><span className="text-korinth-muted">Guidance: </span>{card.strike_expiry_guidance}</div>
      )}
      {card.risk_reward && (
        <div className="text-[11px] text-korinth-text"><span className="text-korinth-muted">Risk/reward: </span>{card.risk_reward}</div>
      )}
      {primary && Array.isArray(card.reasoning_chain) && card.reasoning_chain.length > 0 && (
        <ol className="list-decimal list-inside space-y-0.5 text-[11px] text-korinth-text marker:text-korinth-muted">
          {card.reasoning_chain.map((r, i) => <li key={i}>{r}</li>)}
        </ol>
      )}
      <details className="group">
        <summary className="cursor-pointer text-[10px] font-mono text-korinth-accent inline-flex items-center gap-1 select-none">
          <BookOpen className="w-3 h-3" /> Education + execution
        </summary>
        <div className="mt-1.5 space-y-1.5 pl-1 border-l-2 border-korinth-border">
          <p className="text-[11px] text-korinth-text pl-2 leading-relaxed">{card.education}</p>
          {Array.isArray(card.execution_steps) && (
            <ul className="list-disc list-inside text-[10.5px] text-korinth-muted pl-2 space-y-0.5">
              {card.execution_steps.map((s, i) => <li key={i}>{s}</li>)}
            </ul>
          )}
        </div>
      </details>
    </div>
  );
}

function UnderlyingBlock({ c }) {
  if (c.error) {
    return <div className="rounded border border-korinth-border bg-korinth-surface p-3 text-[11px] font-mono text-korinth-danger">{c.underlying}: {c.error}</div>;
  }
  const iv = c.vol_environment?.iv_level;
  const kv = c.kairos_view;
  return (
    <div className="rounded-lg border border-korinth-border bg-korinth-surface p-3 space-y-2">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-base font-bold text-white">{c.underlying}</span>
          {c.held && <span className="text-[9px] font-mono font-bold uppercase px-1.5 py-0.5 rounded text-emerald-400 bg-emerald-400/10">held</span>}
          <span className="text-[10px] font-mono font-bold uppercase" style={{ color: BIAS_COLOR[c.bias] || MUTED }}>{c.bias}</span>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {c.price != null && <Chip label="px" value={`$${c.price}`} />}
          <Chip label="IV" value={iv} color={IV_COLOR[iv]} />
          {c.vol_environment?.vix != null && <Chip label="VIX" value={c.vol_environment.vix} />}
          {c.regime?.regime && <Chip label="regime" value={c.regime.regime} />}
        </div>
      </div>
      {kv && (
        <div className="text-[10.5px] font-mono text-korinth-muted">
          KAIROS: {kv.direction}{kv.timeframe ? ` · ${kv.timeframe}` : ''}{kv.confidence != null ? ` · ${kv.confidence}%` : ''}{kv.conviction != null ? ` · conv ${kv.conviction}/10` : ''}{kv.target_price != null ? ` · tgt $${kv.target_price}` : ''}
        </div>
      )}
      {c.nearest_catalyst && (
        <div className="text-[10.5px] font-mono text-korinth-muted">Catalyst: {c.nearest_catalyst.description || c.nearest_catalyst.type} (~{c.nearest_catalyst.days_out}d)</div>
      )}
      <StrategyCard card={c.recommendation} primary />
      {Array.isArray(c.alternatives) && c.alternatives.map((a, i) => <StrategyCard key={i} card={a} primary={false} />)}
      {c.data_note && <p className="text-[10px] font-mono text-korinth-muted italic">{c.data_note}</p>}
    </div>
  );
}

export default function OptionsStrategyPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const r = await fetch('/api/options/strategies');
      const d = await r.json();
      if (!r.ok) { setErr(d.error || `HTTP ${r.status}`); return; }
      setData(d);
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(load, 120000);
    return () => clearInterval(iv);
  }, [load]);

  const candidates = data?.candidates || [];

  return (
    <ModuleContainer
      title="Options Strategy Engine"
      subtitle="thesis-to-trade + education · rule-based structural guidance"
      icon={<Activity className="w-3.5 h-3.5" />}
    >
      {loading && !data && <div className="text-[11px] font-mono text-korinth-muted animate-pulse">Building strategies…</div>}
      {err && <div className="text-[11px] font-mono text-korinth-danger">Error: {err} <button onClick={load} className="ml-2 underline">retry</button></div>}
      {data && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-[10px] font-mono text-korinth-muted">
            <Clock className="w-3 h-3" /> {data.as_of ? new Date(data.as_of).toISOString().slice(0, 16).replace('T', ' ') : '—'} UTC · {data.count} underlyings
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {candidates.map((c, i) => <UnderlyingBlock key={c.underlying || i} c={c} />)}
          </div>
          <p className="text-[10px] font-mono text-korinth-muted italic border-t border-korinth-border pt-2">
            Structural guidance only. KORINTH has no live options-chain / IV / Greeks feed (deferred paid sources), so strikes/expiries are anchored to KAIROS levels + the market vol regime — not live-priced premiums. Execute and price in IBKR.
          </p>
        </div>
      )}
    </ModuleContainer>
  );
}
